import React from 'react';
import { Line, LineChart, ResponsiveContainer, Tooltip } from 'recharts';
import { bokunTheme } from '../../../../index';
import { AppDashboardNamedDataType } from '../../../../types/generated';
import styled from 'styled-components';

const StyledTooltip = styled.div`
    padding: 3px 6px;
    font-size: 12px;
    font-weight: bold;
    background: #fff;
    border-radius: 3px;
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.09);
`;

interface LineChartComponentProps {
    data: AppDashboardNamedDataType[];
    dataKey: string;
    secondaryData?: AppDashboardNamedDataType[];
    secondaryDataKey?: string;
    reset?: () => void;
    onHover?: (payload: any[] | null) => void;
    tooltipLabelFormatter?: (data: string) => string;
    tooltipDataKey?: string;
}

export const LineChartComponent = ({
    data,
    dataKey,
    secondaryData,
    secondaryDataKey,
    onHover,
    reset,
    tooltipLabelFormatter,
    tooltipDataKey,
}: LineChartComponentProps): JSX.Element => {
    const hasSecondaryData = secondaryDataKey && secondaryData.length === data.length;
    const chartData = hasSecondaryData
        ? data.map((d, dIndex) => ({
              [dataKey]: d.amount,
              [secondaryDataKey]: secondaryData[dIndex].amount,
              label: d.label,
          }))
        : data.map(d => ({ [dataKey]: d.amount, label: d.label }));

    return (
        <div onMouseLeave={reset}>
            <ResponsiveContainer height={150} width={'100%'}>
                <LineChart data={chartData}>
                    {hasSecondaryData && (
                        <Line
                            dot={false}
                            type="monotone"
                            dataKey={secondaryDataKey}
                            fill="transparent"
                            stroke={bokunTheme.chart.negative}
                            strokeWidth={2}
                        />
                    )}
                    <Line
                        dot={false}
                        type="monotone"
                        dataKey={dataKey}
                        fill="transparent"
                        stroke={bokunTheme.primary}
                        strokeWidth={2}
                    />
                    <Tooltip
                        content={tooltip => {
                            const { active, payload } = tooltip;
                            if (onHover) {
                                if (active && payload) {
                                    onHover(payload.reverse());
                                } else {
                                    onHover(null);
                                }
                            }

                            if (tooltipDataKey && payload.length > 0) {
                                const { payload: payloadData } = payload[0];
                                const string = tooltipLabelFormatter
                                    ? tooltipLabelFormatter(payloadData[tooltipDataKey])
                                    : payloadData[tooltipDataKey];
                                return <StyledTooltip>{string}</StyledTooltip>;
                            }

                            return null;
                        }}
                    />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
};
